function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { isError, isPlainObject, isParameterizedString } from './is.js';
import { addExceptionTypeValue, addExceptionMechanism } from './misc.js';
import { normalizeToSize } from './normalize.js';
import { extractExceptionKeysForMessage } from './object.js';
/**
 * Extracts stack frames from the error.stack string
 */

function parseStackFrames(stackParser, error) {
  return stackParser(error.stack || '', 1);
}
/**
 * Extracts stack frames from the error and builds a Sentry Exception
 */


function exceptionFromError(stackParser, error) {
  var exception = {
    type: error.name || error.constructor.name,
    value: error.message
  };
  var frames = parseStackFrames(stackParser, error);

  if (frames.length) {
    exception.stacktrace = {
      frames: frames
    };
  }

  return exception;
}

function getMessageForObject(exception) {
  if ('name' in exception && typeof exception.name === 'string') {
    var message = "'".concat(exception.name, "' captured as exception");

    if ('message' in exception && typeof exception.message === 'string') {
      message += " with message '".concat(exception.message, "'");
    }

    return message;
  } else if ('message' in exception && typeof exception.message === 'string') {
    return exception.message;
  } else {
    // This will allow us to group events based on top-level keys
    // which is much better than creating new group when any key/value change
    return "Object captured as exception with keys: ".concat(extractExceptionKeysForMessage(exception));
  }
}
/**
 * Builds and Event from a Exception
 *
 * TODO(v8): Remove getHub fallback
 * @hidden
 */


function eventFromUnknownInput(getHubOrClient, stackParser, exception, hint) {
  var client = typeof getHubOrClient === 'function' ? // eslint-disable-next-line deprecation/deprecation
  getHubOrClient().getClient() : getHubOrClient;
  var ex = exception;
  var providedMechanism = hint && hint.data && hint.data.mechanism;
  var mechanism = providedMechanism || {
    handled: true,
    type: 'generic'
  };
  var extras;

  if (!isError(exception)) {
    if (isPlainObject(exception)) {
      var normalizeDepth = client && client.getOptions().normalizeDepth;
      extras = _defineProperty({}, '__serialized__', normalizeToSize(exception, normalizeDepth));
      var message = getMessageForObject(exception);
      ex = hint && hint.syntheticException || new Error(message);
      ex.message = message;
    } else {
      // This handles when someone does: `throw "something awesome";`
      // We use synthesized Error here so we can extract a (rough) stack trace.
      ex = hint && hint.syntheticException || new Error(exception);
      ex.message = exception;
    }

    mechanism.synthetic = true;
  }

  var event = {
    exception: {
      values: [exceptionFromError(stackParser, ex)]
    }
  };

  if (extras) {
    event.extra = extras;
  }

  addExceptionTypeValue(event, undefined, undefined);
  addExceptionMechanism(event, mechanism);
  return _objectSpread(_objectSpread({}, event), {}, {
    event_id: hint && hint.event_id
  });
}
/**
 * Builds and Event from a Message
 * @hidden
 */


function eventFromMessage(stackParser, message) {
  var level = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'info';
  var hint = arguments.length > 3 ? arguments[3] : undefined;
  var attachStacktrace = arguments.length > 4 ? arguments[4] : undefined;
  var event = {
    event_id: hint && hint.event_id,
    level: level
  };

  if (attachStacktrace && hint && hint.syntheticException) {
    var frames = parseStackFrames(stackParser, hint.syntheticException);

    if (frames.length) {
      event.exception = {
        values: [{
          value: message,
          stacktrace: {
            frames: frames
          }
        }]
      };
    }
  }

  if (isParameterizedString(message)) {
    var __sentry_template_string__ = message.__sentry_template_string__,
        __sentry_template_values__ = message.__sentry_template_values__;
    event.logentry = {
      message: __sentry_template_string__,
      params: __sentry_template_values__
    };
    return event;
  }

  event.message = message;
  return event;
}

export { eventFromMessage, eventFromUnknownInput, exceptionFromError, parseStackFrames };